// File: frontend/src/pages/Capabilities/components/DashboardCards.js

import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
import styles from './DashboardCards.module.css';

// Import icons from react-icons
import { 
  FiTool, 
  FiShoppingBag, 
  FiRefreshCw,
  FiUsers, 
  FiSettings, 
  FiPieChart,
  FiShield, 
  FiCpu, 
  FiHome, 
  FiBarChart2,
  FiDatabase,
  FiActivity,
  FiMapPin,
  FiTarget,
  FiTruck,
  FiNavigation,
  FiTrendingUp
} from 'react-icons/fi';

const DashboardCards = () => {
  const { t } = useLanguage();
  const scrollContainerRef = useRef(null);
  
  // Define color schemes from the color system
  const colorSchemes = [
    {
      color: 'var(--color-blue-310)',
      bgColor: 'rgba(235, 241, 255, 0.15)',
      borderColor: 'var(--color-blue-310)'
    },
    {
      color: 'var(--color-green-310)',
      bgColor: 'rgba(239, 250, 246, 0.15)',
      borderColor: 'var(--color-green-310)'
    },
    {
      color: 'var(--color-orange-310)',
      bgColor: 'rgba(254, 243, 235, 0.15)',
      borderColor: 'var(--color-orange-310)'
    },
    {
      color: 'var(--color-yellow-310)',
      bgColor: 'rgba(254, 247, 236, 0.15)',
      borderColor: 'var(--color-yellow-310)'
    },
    {
      color: 'var(--color-red-310)',
      bgColor: 'rgba(253, 237, 240, 0.15)',
      borderColor: 'var(--color-red-310)'
    },
    {
      color: 'var(--color-primary-600)',
      bgColor: 'rgba(239, 244, 255, 0.15)',
      borderColor: 'var(--color-primary-600)'
    },
    {
      color: 'var(--color-green-420)',
      bgColor: 'rgba(217, 237, 146, 0.15)',
      borderColor: 'var(--color-green-420)'
    },
    {
      color: 'var(--color-blue-620)',
      bgColor: 'rgba(153, 217, 140, 0.15)',
      borderColor: 'var(--color-blue-620)'
    }
  ];
  
  // Define capabilities as individual cards
  const capabilities = [
    {
      id: 'build_insurance_products',
      title: t('build_insurance_products_title', 'Build Insurance Products'),
      description: t('build_insurance_products_desc', 'Design, customize, and launch insurance products in minutes with our no-code platform'),
      icon: <FiTool size={32} />,
      colorScheme: 0 // Blue
    },
    {
      id: 'embedded_insurance',
      title: t('embedded_insurance', 'Embedded Insurance'),
      description: t('embedded_insurance_desc', 'Seamlessly integrate insurance with other products and services'),
      icon: <FiDatabase size={32} />,
      colorScheme: 1 // Green
    },
    {
      id: 'usage_based_insurance',
      title: t('usage_based_insurance', 'Usage-based Insurance'),
      description: t('usage_based_insurance_desc', 'Create policies based on actual usage patterns and driving behavior'),
      icon: <FiActivity size={32} />,
      colorScheme: 2 // Orange
    },
    {
      id: 'location_based_insurance',
      title: t('location_based_insurance', 'Location-based Insurance'),
      description: t('location_based_insurance_desc', 'Customize insurance based on geographic location and risk factors'),
      icon: <FiMapPin size={32} />,
      colorScheme: 3 // Yellow
    },
    {
      id: 'new_business',
      title: t('new_business_title', 'New Business'),
      description: t('new_business_desc', 'Acquire and onboard new customers with targeted risk assessment and streamlined tools'),
      icon: <FiShoppingBag size={32} />,
      colorScheme: 4 // Red
    },
    {
      id: 'property_risk_analysis',
      title: t('property_risk_analysis', 'Property Risk Analysis'),
      description: t('property_risk_analysis_desc', 'Analyze property data to accurately assess policy pricing'),
      icon: <FiHome size={32} />,
      colorScheme: 5 // Primary
    },
    {
      id: 'vehicle_risk_analysis',
      title: t('vehicle_risk_analysis', 'Vehicle Risk Analysis'),
      description: t('vehicle_risk_analysis_desc', 'Analyze vehicle data to accurately assess policy pricing'),
      icon: <FiTruck size={32} />,
      colorScheme: 6 // Green 420
    },
    {
      id: 'risk_based_targeting',
      title: t('risk_based_targeting', 'Risk-based Targeting'),
      description: t('risk_based_targeting_desc', 'Identify and target written risk profile'),
      icon: <FiTarget size={32} />,
      colorScheme: 7 // Blue 620
    },
    {
      id: 'renewal',
      title: t('renewal_title', 'Renewal'),
      description: t('renewal_desc', 'Optimize customer retention and portfolio value with data-driven insights'),
      icon: <FiRefreshCw size={32} />,
      colorScheme: 0 // Blue
    },
    {
      id: 'cross_sell_upsell',
      title: t('cross_sell_upsell', 'Cross-sell & Upsell'),
      description: t('cross_sell_upsell_desc', 'Identify opportunities to offer additional products'),
      icon: <FiTrendingUp size={32} />,
      colorScheme: 1 // Green
    },
    {
      id: 'exposure_portfolio_analysis',
      title: t('exposure_portfolio_analysis', 'Exposure & Portfolio Analysis'),
      description: t('exposure_portfolio_analysis_desc', 'Analyze entire insurance portfolio'),
      icon: <FiPieChart size={32} />,
      colorScheme: 2 // Orange
    },
    {
      id: 'trip_tracking',
      title: t('trip_tracking', 'Trip Tracking'),
      description: t('trip_tracking_desc', 'Monitor customer driving patterns and trip data'),
      icon: <FiNavigation size={32} />,
      colorScheme: 3 // Yellow
    },
    {
      id: 'customer_management',
      title: t('customer_management_title', 'Customer Management'),
      description: t('customer_management_desc', 'Manage customer relationships, quotes, and account information'),
      icon: <FiUsers size={32} />,
      colorScheme: 4 // Red
    },
    {
      id: 'operations_management',
      title: t('operations_management_title', 'Operations Management'),
      description: t('operations_management_desc', 'Streamline policy administration, claims processing, and document management'),
      icon: <FiSettings size={32} />,
      colorScheme: 5 // Primary
    },
    {
      id: 'business_intelligence',
      title: t('business_intelligence_title', 'Business Intelligence'),
      description: t('business_intelligence_desc', 'Access financial insights, reporting tools, and marketing analytics'),
      icon: <FiPieChart size={32} />,
      colorScheme: 6 // Green 420
    },
    {
      id: 'admin_tools',
      title: t('admin_tools_title', 'Admin Tools'),
      description: t('admin_tools_desc', 'Access system configuration, source code, and administrative functions'),
      icon: <FiShield size={32} />,
      colorScheme: 7 // Blue 620
    },
    {
      id: 'ai_tools',
      title: t('ai_tools_title', 'AI Tools'),
      description: t('ai_tools_desc', 'Leverage artificial intelligence for customer service and decision support'),
      icon: <FiCpu size={32} />,
      colorScheme: 0 // Blue
    },
    {
      id: 'advanced_analytics',
      title: t('advanced_analytics_title', 'Advanced Analytics'),
      description: t('advanced_analytics_desc', 'Gain deeper insights into driving behavior, mileage, and vehicle performance'),
      icon: <FiBarChart2 size={32} />,
      colorScheme: 1 // Green
    }
  ];
  
  // Update scroll indicator position
  const updateScrollIndicator = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const thumbWidth = (clientWidth / scrollWidth) * 100;
    const thumbLeft = (scrollLeft / scrollWidth) * 100;
    
    const scrollThumb = document.querySelector(`.${styles.scrollThumb}`);
    if (scrollThumb) {
      scrollThumb.style.width = `${thumbWidth}%`;
      scrollThumb.style.left = `${thumbLeft}%`;
    }
  };
  
  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', updateScrollIndicator);
      // Initial update
      updateScrollIndicator();
      
      return () => scrollContainer.removeEventListener('scroll', updateScrollIndicator);
    }
  }, []);

  return (
    <div className={styles.dashboardCardsContainer}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('capabilities.dashboard.title', 'Explore Our Platform')}</h2>
        <p className={styles.sectionDescription}>{t('capabilities.dashboard.description', 'Discover the key components of Allegory\'s insurance technology platform')}</p>
      </div>
      
      <div className={styles.cardsScrollContainer} ref={scrollContainerRef}>
        <div className={styles.cardsWrapper}>
          {capabilities.map((capability, index) => {
            const scheme = colorSchemes[capability.colorScheme];
            
            return (
              <motion.div
                key={capability.id}
                className={styles.card}
                style={{ 
                  background: scheme.bgColor,
                  borderLeft: `4px solid ${scheme.borderColor}`
                }}
                whileHover={{ y: -8, boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2)' }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <div className={styles.cardIcon} style={{ color: scheme.color }}>
                  {capability.icon}
                </div>
                <h3 className={styles.cardTitle}>{capability.title}</h3>
                <p className={styles.cardDescription}>{capability.description}</p>
              </motion.div>
            );
          })}
          
          <div className={styles.cardSpacer}></div>
        </div>
      </div>
      
      <div className={styles.scrollIndicator}>
        <div className={styles.scrollTrack}>
          <div className={styles.scrollThumb} />
        </div>
      </div>
    </div>
  );
};

export default DashboardCards;